import {
    Box,
    Breadcrumbs,
    Button,
    Divider,
    Grid2,
    IconButton,
    Link,
    Snackbar,
    Typography,
    Alert,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions
} from '@mui/material';
import { Icon } from '@iconify/react/dist/iconify.js';
import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import CloseIcon from '@mui/icons-material/Close';
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import EditIcon from '@mui/icons-material/Edit';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import StarIcon from '@mui/icons-material/Star';
import StarHalfIcon from '@mui/icons-material/StarHalf';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Dashboard_URL, Page_Padding, Skillvation_Dashboard_URL } from '../../shared/constants';
import AddressFormDialog from '../../shared/components/AddressFormDialog';
import { useSkillBoxCart } from '../../assets/nodeApi/getApi/getCommonApi';
import { useAllSkillBoxProducts } from '../../assets/api/useGetApi';
import { postCart, postPurchaseOrder, postVerifyPurchase, postOrderDetails, postShippingMethods } from '../../assets/nodeApi/postApi/postCommonApi';
import { deleteCart } from '../../assets/nodeApi/deleteApi/deleteAPi';
import { useEffect, useMemo, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import debounce from 'lodash.debounce';
import { useSkillBoxAddress, useStateCodeByCountry } from '../../assets/nodeApi/getApi/getCommonApi';
import { postAddress } from '../../assets/nodeApi/postApi/postCommonApi';
import { putAddress } from '../../assets/nodeApi/putApi/putSchoolApi';
import { deleteAddress } from '../../assets/nodeApi/deleteApi/deleteAPi';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { RadioFormField } from '../../shared/components/FormFields/RadioFormField';
import { useMutation } from '@tanstack/react-query';
import useAuthStore from '../../shared/store/useAuthStore';
import { TextFormField } from '../../shared/components/FormFields/TextFormField';
import { useToaster } from '../../shared/utils/ToasterProvider';
import useVisitedPathStore from '../../shared/store/useVisitedPathStore';

// ── Helper ────────────────────────────────────────────────────────────────────
const resolveImage = (image_small) => {
    if (!image_small) return '';
    if (image_small.startsWith('http://') || image_small.startsWith('https://')) return image_small;
    return `https://myskill.club/shop/uploads/images/${image_small}`;
};

const StarRating = ({ rating = 0 }) => {
    const stars = Array.from({ length: 5 }, (_, i) => {
        const filled = i + 1 <= Math.floor(rating);
        const half = !filled && i < rating;
        return filled ? 'full' : half ? 'half' : 'empty';
    });
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
            {stars.map((type, i) =>
                type === 'full' ? (
                    <StarIcon key={i} sx={{ fontSize: '14px', color: '#F5A623' }} />
                ) : type === 'half' ? (
                    <StarHalfIcon key={i} sx={{ fontSize: '14px', color: '#F5A623' }} />
                ) : (
                    <StarBorderIcon key={i} sx={{ fontSize: '14px', color: '#F5A623' }} />
                )
            )}
            <Typography variant="caption" color="grey.500" sx={{ ml: 0.5 }}>
                {rating} ratings
            </Typography>
        </Box>
    );
};

// ── Dummy Coupons ─────────────────────────────────────────────────────────────
const AVAILABLE_COUPONS = [
    { code: 'START100', label: 'START100', description: 'Apply and get 100 rupees off', discount: 100 },
    { code: 'SAVE50', label: 'SAVE50', description: 'Apply and get 50 rupees off', discount: 50 },
];

const TAX_RATE = 0;

// ── Cart Item Row ─────────────────────────────────────────────────────────────
const CartItem = ({ item }) => {
    const imageUrl = resolveImage(item?.image_small);
    const queryClient = useQueryClient();
    const [localQty, setLocalQty] = useState(item?.qty || 1);

    useEffect(() => {
        setLocalQty(item?.qty || 1);
    }, [item?.qty]);

    const flushQty = useMemo(
        () =>
            debounce((productId, finalQty) => {
                postCart({ productId, quantity: finalQty })
                    .then(() => queryClient.invalidateQueries({ queryKey: ["v1/cart"] }))
                    .catch((err) => console.error("Error updating cart API", err));
            }, 600),
        [queryClient]
    );

    // Cleanup debounce on unmount
    useEffect(() => {
        return () => flushQty.cancel();
    }, [flushQty]);

    const handleUpdateQty = (newQty) => {
        if (newQty < 1) return;

        // Update local UI immediately
        setLocalQty(newQty);

        // Debounce the backend update
        if (item?.id) {
            flushQty(item.id, newQty);
        }
    };

    const handleRemove = () => {
        if (item?.id) {
            deleteCart({ productId: item.id })
                .then(() => queryClient.invalidateQueries({ queryKey: ["v1/cart"] }))
                .catch(err => console.error("Error deleting from cart API", err));
        }
    };

    return (
        <Box
            sx={{
                display: 'flex',
                gap: '0.75rem',
                alignItems: { xs: 'flex-start', sm: 'center' },
                flexWrap: { xs: 'wrap', sm: 'nowrap' },
                bgcolor: '#F9F9F9',
                borderRadius: '10px',
                p: '0.75rem',
                position: 'relative',
                border: '1px solid #F0F0F0',
            }}
        >
            {/* Remove button */}
            <IconButton
                id={`remove-cart-item-${item?.slug}`}
                size="small"
                onClick={handleRemove}
                sx={{
                    position: 'absolute',
                    top: 6,
                    right: 6,
                    p: '3px',
                    color: '#888',
                    '&:hover': { color: 'red.main', bgcolor: '#FFF0F0' },
                    transition: 'all 0.2s ease',
                }}
            >
                <CloseIcon sx={{ fontSize: '16px' }} />
            </IconButton>

            {/* Image */}
            <Box
                sx={{
                    width: { xs: '64px', sm: '76px' },
                    height: { xs: '64px', sm: '76px' },
                    borderRadius: '8px',
                    bgcolor: '#eee',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    overflow: 'hidden',
                }}
            >
                <Box
                    component="img"
                    src={imageUrl}
                    alt={item?.course_name}
                    sx={{ width: '85%', height: '85%', objectFit: 'contain', borderRadius: '0px' }}
                />
            </Box>

            {/* Info */}
            <Box sx={{ flex: 1, minWidth: 0, pr: { xs: '1.5rem', sm: '1.5rem' } }}>
                <Typography variant="body2" fontWeight={600} noWrap>{item?.course_name}</Typography>
                <Typography variant="caption" color="grey.500" noWrap sx={{ display: 'block' }}>
                    {item?.short_description
                        ? (item.short_description.length > 40
                            ? item.short_description.substring(0, 40) + '...'
                            : item.short_description)
                        : ''}
                    {item?.size && ` · Size: ${item.size}`}
                </Typography>
                <Box mt={0.4}>
                    <StarRating rating={item?.rating} />
                </Box>
            </Box>

            {/* Qty Controls + Price row - stacks on mobile */}
            <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: { xs: 'space-between', sm: 'flex-end' },
                gap: { xs: 1, sm: 0.5 },
                width: { xs: '100%', sm: 'auto' },
                mt: { xs: '0.25rem', sm: 0 },
            }}>
                {/* Qty Controls */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                    <Typography variant="caption" color="grey.500" sx={{ mr: 0.5 }}>Qty</Typography>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            border: '1px solid #E0E0E0',
                            borderRadius: '8px',
                            overflow: 'hidden',
                        }}
                    >
                        <IconButton
                            id={`cart-qty-decrease-${item?.slug}`}
                            size="small"
                            onClick={() => handleUpdateQty(localQty - 1)}
                            sx={{
                                borderRadius: 0,
                                bgcolor: 'red.main',
                                color: '#fff',
                                p: '5px',
                                '&:hover': { bgcolor: '#b01030' },
                            }}
                        >
                            <RemoveIcon sx={{ fontSize: '14px' }} />
                        </IconButton>
                        <Typography variant="body2" sx={{ px: '10px', fontWeight: 600, minWidth: '28px', textAlign: 'center' }}>
                            {localQty}
                        </Typography>
                        <IconButton
                            id={`cart-qty-increase-${item?.slug}`}
                            size="small"
                            onClick={() => handleUpdateQty(localQty + 1)}
                            sx={{
                                borderRadius: 0,
                                bgcolor: 'red.main',
                                color: '#fff',
                                p: '5px',
                                '&:hover': { bgcolor: '#b01030' },
                            }}
                        >
                            <AddIcon sx={{ fontSize: '14px' }} />
                        </IconButton>
                    </Box>
                </Box>

                {/* Price */}
                <Box sx={{ flexShrink: 0, textAlign: 'right', minWidth: '72px', ml: { sm: '0.75rem' } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                        <CurrencyRupeeIcon sx={{ color: 'red.main', fontSize: '15px' }} />
                        <Typography variant="body2" fontWeight={700} color="red.main">
                            {(parseFloat(item?.price_discounted || 0) * localQty)}
                        </Typography>
                    </Box>
                    <Typography variant="caption" color="grey.400">
                        ₹ {item?.price_discounted}
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
};

// ── Price Row ─────────────────────────────────────────────────────────────────
const PriceRow = ({ label, value, isTotal = false, isDiscount = false, color }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: '4px' }}>
        <Typography
            variant={isTotal ? 'body1' : 'body2'}
            fontWeight={isTotal ? 500 : 300}
            color={isDiscount ? 'red.main' : 'inherit'}
        >
            {label}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {isDiscount && <Typography variant="body2" color="red.main" mr={0.3}>- </Typography>}
            <CurrencyRupeeIcon sx={{ fontSize: isTotal ? '16px' : '14px', color: isDiscount ? 'red.main' : color || 'inherit' }} />
            <Typography
                variant={isTotal ? 'body1' : 'body2'}
                fontWeight={isTotal ? 500 : 300}
                color={isDiscount ? 'red.main' : color || 'inherit'}
            >
                {value}
            </Typography>
        </Box>
    </Box>
);

// ── Main Component ────────────────────────────────────────────────────────────
const SkillBoxCart = () => {
    const navigate = useNavigate();
    const location = useLocation();

    const sourcePath = location.state?.sourcePath || '/SkillBox';
    const sourceName = location.state?.sourceName || 'Skill Box';

    const [appliedCoupon, setAppliedCoupon] = useState(null);

    const [addressDialogOpen, setAddressDialogOpen] = useState(false);
    const [editingAddress, setEditingAddress] = useState(null);

    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [addressToDelete, setAddressToDelete] = useState(null);


    // ── API Fetching & Syncing ────────────────────────────────────────────────
    const queryClient = useQueryClient();
    const user = useAuthStore((state) => state.user);
    const { data: cartData, isLoading: isCartLoading } = useSkillBoxCart();
    const { data: productsData, isLoading: isProductsLoading } = useAllSkillBoxProducts();
    const { data: addressData, isLoading: isAddressLoading } = useSkillBoxAddress(user?.role);

    const addresses = addressData?.data || [];

    const [selectedAddressId, setSelectedAddressId] = useState(null);
    const deliveryAddress = addresses.find(a => a.id === selectedAddressId) || addresses[0];

    useEffect(() => {
        if (addresses && addresses.length > 0) {
            const isValid = addresses.some(a => a.id === selectedAddressId);
            if (!isValid) {
                setSelectedAddressId(addresses[0].id);
            }
        } else if (addresses && addresses.length === 0 && selectedAddressId !== null) {
            setSelectedAddressId(null);
        }
    }, [addresses, selectedAddressId]);

    const deliveryState = deliveryAddress?.state || '';
    const { data: stateCodeData } = useStateCodeByCountry(99, deliveryState);
    const stateId = stateCodeData?.data?.[0]?.id || deliveryAddress?.state;

    const [shippingCharge, setShippingCharge] = useState(0);

    const cartItemsStr = JSON.stringify(cartData?.data || []);
    useEffect(() => {
        const cartItems = JSON.parse(cartItemsStr);
        if (stateId && cartItems.length > 0) {
            const product_ids = cartItems.map(item => item.product_id);
            const quantities = cartItems.map(item => item.quantity);
            const payload = {
                product_ids,
                quantities,
                country_id: 99,
                state_id: stateId
            };
            postShippingMethods(payload)
                .then(res => {
                    const shippingCost = res?.total_shipping || res?.data?.total_shipping;
                    if (shippingCost) {
                        setShippingCharge(parseFloat(shippingCost));
                    } else {
                        setShippingCharge(0);
                    }
                })
                .catch(err => {
                    console.error("Error fetching shipping cost", err);
                    setShippingCharge(0);
                });
        } else {
            setShippingCharge(0);
        }
    }, [stateId, cartItemsStr]);

    // Map API data to display items
    const displayItems = useMemo(() => {
        if (cartData?.data && productsData?.data) {
            return cartData.data.map(c => {
                const pId = c.product_id || c.productId || c.product?.id || c.id;
                const p = productsData.data.find(p => String(p.id) === String(pId));
                if (!p) return null;

                return {
                    ...p,
                    course_name: p.title || p.course_name,
                    image_small: p.image || p.image_small,
                    qty: c.quantity || 1,
                    short_description: p.short_description,
                    cart_item_id: c.id
                };
            }).filter(Boolean);
        }
        return [];
    }, [cartData, productsData]);
    const [couponInput, setCouponInput] = useState('');
    const [couponError, setCouponError] = useState('');
    const [snackbar, setSnackbar] = useState({ open: false, msg: '', type: 'success' });

    const token = useAuthStore((state) => state.accessToken);
    const { showToaster } = useToaster();

    const createOrderMutation = useMutation({
        mutationFn: (formData) => postPurchaseOrder(formData),
    });

    const handleRazorpay = (values) => {
        if (!token) {
            useVisitedPathStore.getState().setLastVisitedPath(location.pathname);
            localStorage.setItem("lastVisitedPath", location.pathname);
            navigate("/StudentLogin");
            return;
        }

        if (!selectedAddressId) {
            showToaster('Warning!', 'Please select a delivery address', 'warning');
            return;
        }

        const obj = {
            "addressId": selectedAddressId,
            "country_id": 99,
            "state_id": stateId,
            "shippingCharge": shippingCharge.toFixed(2),
            "items": displayItems.map(item => ({
                "productId": item.id,
                "quantity": item.qty
            }))
        };

        createOrderMutation.mutate(obj, {
            onSuccess: (response) => {
                const orderData = response?.data || response;

                if (!orderData?.order?.id) {
                    showToaster('Warning!', 'Failed to get order ID from server', 'warning');
                    return;
                }

                console.log("paymentResponse", response)


                const options = {
                    key: response?.key || orderData?.key || response?.data?.key,
                    amount: orderData?.order?.amount || orderData?.amount,
                    currency: orderData?.order?.currency || orderData?.currency || "INR",
                    name: "Myskill.club",
                    description: "Payment for order",
                    order_id: response?.payment_order_id || orderData?.payment_order_id || orderData?.order?.payment_order_id || orderData?.order?.id,
                    handler: async (paymentResponse) => {
                        const paymentData = {
                            razorpay_payment_id: paymentResponse.razorpay_payment_id,
                            razorpay_order_id: paymentResponse.razorpay_order_id,
                            razorpay_signature: paymentResponse.razorpay_signature,
                            purchaseId: response?.purchaseId || orderData?.purchaseId || response?.data?.purchaseId || orderData?.order?.id,
                        };
                        try {
                            const verifyResponse = await postVerifyPurchase(paymentData);
                            if (verifyResponse?.data?.success || verifyResponse?.success || verifyResponse?.data?.message) {
                                const transactionId = verifyResponse?.data?.razorpay_payment_id || verifyResponse?.razorpay_payment_id || paymentData?.razorpay_payment_id;
                                const orderDetailsPayload = {
                                    items: displayItems.map(item => ({
                                        product_id: item.id,
                                        quantity: item.qty
                                    })),
                                    payment_method: "razorpay",
                                    transaction_id: transactionId,
                                    name: deliveryAddress?.full_name,
                                    email: user?.email,
                                    phone: deliveryAddress?.phone,
                                    address: deliveryAddress?.address_line_1,
                                    city: deliveryAddress?.city,
                                    state: stateId,
                                    country: deliveryAddress?.country || "India",
                                    zip_code: deliveryAddress?.postal_code || "",
                                    currency: "INR"
                                };
                                await postOrderDetails(orderDetailsPayload);

                                showToaster('Success', 'Payment successful', 'success', {
                                    showLink: true,
                                    linkText: "Click to view your order history",
                                    onClickLink: () => window.location.href = user?.role === 'student'
                                        ? `${Dashboard_URL}student/orderhistory`
                                        : `${Skillvation_Dashboard_URL}order-history`,
                                    autoClose: false
                                });
                                queryClient.invalidateQueries({ queryKey: ["v1/cart"] });
                                navigate('/Cartpage');
                            } else {
                                showToaster('Error', verifyResponse?.data?.message || verifyResponse?.message || 'Payment verification failed', 'error');
                            }
                        } catch (error) {
                            showToaster('Error', error.response?.data?.message || error.message || 'Payment error', 'error');
                        }
                    },
                    theme: { color: "#DC143C" }
                };
                const rzp = new window.Razorpay(options);
                rzp.on('payment.failed', function (response) {
                    showToaster('Error', response.error.description || 'Payment failed', 'error');
                    if (response.error.description && response.error.description.includes('already paid')) {
                        queryClient.invalidateQueries({ queryKey: ["v1/cart"] });
                        navigate('/');
                    }
                });
                rzp.open();
            },
            onError: (error) => {
                showToaster('Error', error.response?.data?.error?.description || error.response?.data?.message || error.message || "Payment initiation failed", 'error');
            },
        });
    };

    const handleFinalSubmit = (values) => {
        if (values.paymentMode === 'Online') {
            handleRazorpay(values);
        } else if (values.paymentMode === 'Bank') {
            showToaster('Info', 'Verifying Bank Payment...', 'info');
            // bank order post logic here
        } else if (values.paymentMode === 'Cash') {
            showToaster('Success', 'Placing order for Cash...', 'success');
            // cash order post logic here
        }
    };

    // ── Calculations ──────────────────────────────────────────────────────────
    const subtotal = displayItems.reduce(
        (sum, item) => sum + parseFloat(item?.price_discounted || 0) * (item?.qty || 1),
        0
    );
    const totalTax = displayItems.reduce(
        (sum, item) => sum + parseFloat(item?.tax_amount || 0) * (item?.qty || 1),
        0
    );
    const couponDiscount = appliedCoupon?.discount || 0;
    const total = subtotal + shippingCharge + totalTax - couponDiscount;

    // ── Coupon ────────────────────────────────────────────────────────────────
    const handleApplyCoupon = (code) => {
        const target = code || couponInput;
        const found = AVAILABLE_COUPONS.find((c) => c.code === target.toUpperCase().trim());
        if (found) {
            setAppliedCoupon({ code: found.code, discount: found.discount });
            setCouponError('');
            setSnackbar({ open: true, msg: `Coupon "${found.code}" applied! ₹${found.discount} off.`, type: 'success' });
        } else {
            setCouponError('Invalid coupon code. Please try again.');
        }
    };

    const handleRemoveCoupon = () => {
        setAppliedCoupon(null);
        setCouponInput('');
        setCouponError('');
    };

    // ── Address Handlers ──────────────────────────────────────────────────────
    const handleSaveAddress = async (values) => {
        const payload = {
            full_name: values.full_name,
            phone: values.contactNumber,
            address_line_1: values.address,
            address_line_2: "",
            city: values.city,
            state: values.state,
            country: values.country,
            postal_code: values.pincode,
            address_type: values.address_type,
        };
        try {
            if (editingAddress) {
                await putAddress(editingAddress.id, payload);
                setSnackbar({ open: true, msg: 'Address updated successfully!', type: 'success' });
            } else {
                await postAddress(payload);
                setSnackbar({ open: true, msg: 'Address added successfully!', type: 'success' });
            }
            queryClient.invalidateQueries({ queryKey: ["v1/address"] });
        } catch (err) {
            setSnackbar({ open: true, msg: 'Error saving address', type: 'error' });
        }
    };

    const handleDeleteAddressClick = (id) => {
        setAddressToDelete(id);
        setDeleteDialogOpen(true);
    };

    const handleConfirmDeleteAddress = async () => {
        if (addressToDelete) {
            try {
                await deleteAddress(addressToDelete);
                queryClient.invalidateQueries({ queryKey: ["v1/address"] });
                setSnackbar({ open: true, msg: 'Address deleted successfully!', type: 'success' });
                if (selectedAddressId === addressToDelete) {
                    setSelectedAddressId(null);
                }
            } catch (err) {
                setSnackbar({ open: true, msg: 'Error deleting address', type: 'error' });
            }
        }
        setDeleteDialogOpen(false);
        setAddressToDelete(null);
    };

    // const handleSetDefaultAddress = async (id) => {
    //     try {
    //         await patchSkillBoxAddressDefault(id);
    //         queryClient.invalidateQueries({ queryKey: ["v1/address"] });
    //         setSnackbar({ open: true, msg: 'Default address updated!', type: 'success' });
    //     } catch (err) {
    //         setSnackbar({ open: true, msg: 'Error setting default address', type: 'error' });
    //     }
    // };

    // ── Loading state ─────────────────────────────────────────────────────────
    if (isCartLoading || isProductsLoading || isAddressLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                <CircularProgress sx={{ color: 'red.main' }} />
            </Box>
        );
    }

    // ── Empty state ───────────────────────────────────────────────────────────
    if (displayItems.length === 0) {
        return (
            <Box
                className="body"
                px={Page_Padding}
                sx={{ maxWidth: '1536px', mx: 'auto', pb: '4rem', minHeight: '60vh', display: 'flex', flexDirection: 'column' }}
            >
                <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ py: '1.25rem' }}>
                    <Link underline="hover" onClick={() => navigate('/')} sx={{ cursor: 'pointer', fontSize: '13px', color: '#666' }}>Home</Link>
                    {sourcePath && sourcePath !== '/' && (
                        <Link underline="hover" onClick={() => navigate(sourcePath)} sx={{ cursor: 'pointer', fontSize: '13px', color: '#666' }}>{sourceName}</Link>
                    )}
                    <Link underline="hover" onClick={() => navigate(-1)} sx={{ cursor: 'pointer', fontSize: '13px', color: '#666' }}>Products</Link>
                    <Typography sx={{ fontSize: '13px' }}>Cart</Typography>
                </Breadcrumbs>
                <Box
                    sx={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 2,
                        py: '4rem',
                    }}
                >
                    <Icon icon="f7:cart" width="72" height="72" color="#888" />
                    <Typography variant="h5" fontWeight={600}>Your cart is empty</Typography>
                    <Typography variant="body2" color="grey.500">
                        Browse products and add them to your cart.
                    </Typography>
                    <Button
                        id="go-to-products-btn"
                        variant="contained"
                        onClick={() => navigate(sourcePath)}
                        sx={{
                            mt: 1,
                            bgcolor: 'red.main',
                            color: '#fff',
                            borderRadius: '10px',
                            textTransform: 'none',
                            fontWeight: 600,
                            px: '2rem',
                            '&:hover': { bgcolor: '#b01030' },
                        }}
                    >
                        Browse Products
                    </Button>
                </Box>
            </Box>
        );
    }

    return (
        <Box className="body" px={Page_Padding} sx={{ maxWidth: '1536px', mx: 'auto', pb: '4rem' }}>

            {/* ── Breadcrumb ── */}
            <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ py: '1.25rem' }}>
                <Link underline="hover" onClick={() => navigate('/')} sx={{ cursor: 'pointer', fontSize: '13px', color: '#666' }}>Home</Link>
                {sourcePath && sourcePath !== '/' && (
                    <Link underline="hover" onClick={() => navigate(sourcePath)} sx={{ cursor: 'pointer', fontSize: '13px', color: '#666' }}>{sourceName}</Link>
                )}
                <Link underline="hover" onClick={() => navigate(-1)} sx={{ cursor: 'pointer', fontSize: '13px', color: '#666' }}>Products</Link>
                <Typography sx={{ fontSize: '13px', fontWeight: 600 }}>Cart</Typography>
            </Breadcrumbs>

            <Grid2 container spacing="1.5rem" alignItems="flex-start">

                {/* ── LEFT: Product List + Coupon ── */}
                <Grid2 size={{ xs: 12, md: 7 }} sx={{ order: { xs: 1, md: 1 } }}>
                    <Box
                        sx={{
                            border: '1px solid #E7E7E7',
                            borderRadius: '14px',
                            p: '1.25rem',
                            bgcolor: '#fff',
                        }}
                    >
                        <Typography variant="body1" fontWeight={500} mb={1.5}>
                            Product List
                        </Typography>

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                            {displayItems.map((item, idx) => (
                                <CartItem key={`${item.slug}-${item.size}-${idx}`} item={item} />
                            ))}
                        </Box>

                        {/* <Divider sx={{ my: '1.25rem' }} /> */}

                        {/* ── Coupon Section ── */}
                        {/* <Typography variant="body2" fontWeight={600} mb={1}>
                            Enter your coupon code
                        </Typography> */}

                        {/* <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                            <Box
                                component="input"
                                id="coupon-input"
                                value={couponInput}
                                onChange={(e) => { setCouponInput(e.target.value); setCouponError(''); }}
                                placeholder="Enter Coupon Code"
                                disabled={!!appliedCoupon}
                                sx={{
                                    flex: 1,
                                    height: '44px',
                                    px: '14px',
                                    borderRadius: '10px',
                                    border: couponError ? '1.5px solid' : '1px solid',
                                    borderColor: couponError ? 'red.main' : '#D9D9D9',
                                    outline: 'none',
                                    fontSize: '14px',
                                    color: '#111',
                                    bgcolor: appliedCoupon ? '#F9F9F9' : '#fff',
                                    '&:focus': { borderColor: 'red.main' },
                                    fontFamily: 'inherit',
                                }}
                            />
                            {appliedCoupon ? (
                                <Button
                                    id="remove-coupon-btn"
                                    variant="outlined"
                                    onClick={handleRemoveCoupon}
                                    sx={{
                                        height: '44px',
                                        borderRadius: '10px',
                                        borderColor: 'red.main',
                                        color: 'red.main',
                                        textTransform: 'none',
                                        fontWeight: 600,
                                        flexShrink: 0,
                                        '&:hover': { bgcolor: '#FFF0F0' },
                                    }}
                                >
                                    Remove
                                </Button>
                            ) : (
                                <Button
                                    id="apply-coupon-btn"
                                    variant="outlined"
                                    onClick={() => handleApplyCoupon()}
                                    sx={{
                                        height: '44px',
                                        borderRadius: '10px',
                                        borderColor: 'red.main',
                                        color: 'red.main',
                                        textTransform: 'none',
                                        fontWeight: 600,
                                        flexShrink: 0,
                                        '&:hover': { bgcolor: '#FFF0F0' },
                                    }}
                                >
                                    Apply Coupon
                                </Button>
                            )}
                        </Box>
                        {couponError && (
                            <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                                {couponError}
                            </Typography>
                        )} */}

                        {/* Available Coupons */}
                        {/* {!appliedCoupon && (
                            <Box sx={{ mt: 1.5 }}>
                                <Typography variant="caption" color="grey.500" mb={0.75} display="block">
                                    Select coupon code
                                </Typography>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                    {AVAILABLE_COUPONS.map((c) => (
                                        <Box
                                            key={c.code}
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                bgcolor: '#FFF5F5',
                                                borderRadius: '8px',
                                                px: '12px',
                                                py: '8px',
                                                border: '1px solid #FFE0E0',
                                            }}
                                        >
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Box>
                                                    <Typography variant="caption" fontWeight={700} color="red.main">{c.label}</Typography>
                                                    <Typography variant="caption" color="grey.500" display="block">{c.description}</Typography>
                                                </Box>
                                            </Box>
                                            <Button
                                                id={`apply-coupon-${c.code}`}
                                                size="small"
                                                onClick={() => { setCouponInput(c.code); handleApplyCoupon(c.code); }}
                                                sx={{
                                                    variant: "body2",
                                                    color: 'red.main',
                                                    textTransform: 'none',
                                                    fontWeight: 600,
                                                    fontSize: '12px',
                                                    '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' },
                                                }}
                                            >
                                                Apply Now
                                            </Button>
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                        )} */}
                    </Box>
                </Grid2>

                {/* ── RIGHT: Delivery + Payment ── */}
                <Grid2 size={{ xs: 12, md: 5 }} sx={{ order: { xs: 2, md: 2 } }}>

                    {/* Delivery Location Card */}
                    <Box
                        sx={{
                            border: '1px solid #E7E7E7',
                            borderRadius: '14px',
                            p: '1.25rem',
                            bgcolor: '#fff',
                            mb: '1.25rem',
                        }}
                    >
                        {addresses.length > 0 ? (
                            <>
                                <Typography variant="body2" fontWeight={700} mb={1} color="#111">
                                    Delivery Locations
                                </Typography>
                                {addresses.map((addr) => (
                                    <Box
                                        key={addr.id}
                                        sx={{
                                            mb: 1,
                                            p: 1.5,
                                            border: '1px solid',
                                            borderColor: selectedAddressId === addr.id ? 'red.main' : '#E7E7E7',
                                            borderRadius: '8px',
                                            cursor: 'pointer',
                                            bgcolor: selectedAddressId === addr.id ? '#FFF0F0' : 'transparent',
                                            transition: 'all 0.2s',
                                        }}
                                        onClick={() => setSelectedAddressId(addr.id)}
                                    >
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                            <Box sx={{ flex: 1 }}>
                                                <Typography variant="body2" fontWeight={600} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    {addr.full_name} ({addr.address_type})
                                                </Typography>
                                                <Typography variant="body2" color="grey.600" sx={{ mt: 0.5, lineHeight: 1.5 }}>
                                                    {addr.address_line_1}{addr.address_line_2 ? ', ' + addr.address_line_2 : ''},<br />
                                                    {addr.city}, {addr.state} {addr.postal_code}<br />
                                                    {addr.country}
                                                </Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                <IconButton
                                                    size="small"
                                                    onClick={(e) => { e.stopPropagation(); setEditingAddress(addr); setAddressDialogOpen(true); }}
                                                    sx={{ color: '#888', '&:hover': { color: 'red.main' } }}
                                                >
                                                    <EditIcon sx={{ fontSize: '18px' }} />
                                                </IconButton>
                                                <IconButton
                                                    size="small"
                                                    onClick={(e) => { e.stopPropagation(); handleDeleteAddressClick(addr.id); }}
                                                    sx={{ color: '#888', '&:hover': { color: 'red.main' } }}
                                                >
                                                    <CloseIcon sx={{ fontSize: '18px' }} />
                                                </IconButton>
                                            </Box>
                                        </Box>
                                        {/* {!addr.is_default && (
                                            <Button 
                                                size="small" 
                                                sx={{ mt: 1, textTransform: 'none', fontSize: '12px', color: 'red.main' }} 
                                                onClick={(e) => { e.stopPropagation(); handleSetDefaultAddress(addr.id); }}
                                            >
                                                Set as Default
                                            </Button>
                                        )} */}
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <Typography variant="body2" color="grey.600">
                                                Phone: {addr.phone}
                                            </Typography>
                                            {selectedAddressId === addr.id && (
                                                <CheckCircleIcon sx={{ color: 'red.main', fontSize: '18px' }} />

                                            )}

                                        </Box>

                                    </Box>
                                ))}
                                <Button
                                    variant="outlined"
                                    fullWidth
                                    onClick={() => { setEditingAddress(null); setAddressDialogOpen(true); }}
                                    sx={{ mt: 1, borderColor: 'red.main', color: 'red.main', '&:hover': { bgcolor: '#FFF0F0' }, textTransform: 'none', fontWeight: 600 }}
                                >
                                    Add New Address
                                </Button>
                            </>
                        ) : (
                            <>
                                {/* Add Delivery Location Button */}
                                <Button
                                    id="add-delivery-btn"
                                    fullWidth
                                    variant="outlined"
                                    onClick={() => setAddressDialogOpen(true)}
                                    sx={{
                                        borderColor: 'red.main',
                                        color: 'red.main',
                                        borderRadius: '10px',
                                        textTransform: 'none',
                                        fontWeight: 600,
                                        py: '11px',
                                        fontSize: '15px',
                                        mb: 1.5,
                                        '&:hover': { bgcolor: '#FFF0F0', borderColor: '#b01030' },
                                        transition: 'all 0.2s ease',
                                    }}
                                >
                                    Add Delivery Location
                                </Button>

                                {/* Delivery Info */}
                                <Box
                                    sx={{
                                        display: 'flex',
                                        gap: 1.5,
                                        bgcolor: '#F9F9F9',
                                        borderRadius: '10px',
                                        p: '0.85rem',
                                        border: '1px solid #F0F0F0',
                                    }}
                                >
                                    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.5rem', mb: '0.4rem' }}>
                                            <LocalShippingOutlinedIcon sx={{ color: 'red.main', flexShrink: 0, mt: 0.25 }} />
                                            <Typography variant="body2" fontWeight={600}>Delivery</Typography>
                                        </Box>
                                        <Typography variant="caption" color="grey.500" sx={{ lineHeight: 1.5 }}>
                                            All orders are processed promptly and dispatched with trusted delivery partners. Expect secure packaging and timely arrival.
                                        </Typography>
                                    </Box>
                                </Box>
                            </>
                        )}
                    </Box>

                    {/* Payment Details Card */}
                    <Box
                        sx={{
                            border: '1px solid #E7E7E7',
                            borderRadius: '14px',
                            p: '1.25rem',
                            bgcolor: '#fff',
                        }}
                    >
                        <Typography variant="body2" fontWeight={500} mb={1.5} color="#111">
                            Payment Details
                        </Typography>

                        <PriceRow label="Subtotal" value={subtotal.toFixed(2)} />
                        <PriceRow label="Tax" value={totalTax.toFixed(2)} />
                        <PriceRow label="Shipping Charge" value={shippingCharge.toFixed(2)} />
                        <PriceRow
                            label={`Total`}
                            value={(subtotal + shippingCharge + totalTax).toFixed(2)}
                        />

                        {appliedCoupon && (
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: '4px' }}>
                                <Typography variant="body2" color="red.main" fontWeight={300}>
                                    Coupon applied
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Typography variant="body2" color="red.main">- ₹ {appliedCoupon.discount}</Typography>
                                </Box>
                            </Box>
                        )}

                        <Divider sx={{ my: '0.85rem' }} />

                        <PriceRow label="Total" value={total.toFixed(2)} isTotal color="red.main" />

                        <Formik
                            initialValues={{ paymentMode: 'Online', utrNumber: '' }}
                            // validationSchema={...}
                            onSubmit={(values) => {
                                handleRazorpay(values);
                            }}
                        >
                            {({ values, setFieldValue }) => (
                                <Form>
                                    {/* Payment Mode options hidden
                                    <Box sx={{ mt: 2 }}>
                                        <Field name="paymentMode" ... />
                                    </Box>
                                    */}

                                    <Box sx={{ mt: '1rem' }}>
                                        <Button
                                            id="pay-now-btn"
                                            type="submit"
                                            fullWidth
                                            variant="contained"
                                            disabled={!deliveryAddress || createOrderMutation.isPending}
                                            sx={{
                                                bgcolor: 'red.main',
                                                color: '#fff',
                                                borderRadius: '10px',
                                                py: '13px',
                                                fontSize: '15px',
                                                fontWeight: 700,
                                                textTransform: 'none',
                                                '&:hover': {
                                                    bgcolor: 'red.main',
                                                    boxShadow: '0 4px 14px rgba(220,20,60,0.3)',
                                                    transform: 'translateY(-1px)',
                                                },
                                                '&.Mui-disabled': {
                                                    bgcolor: '#F5A3B0',
                                                    color: '#fff',
                                                },
                                                transition: 'all 0.2s ease',
                                            }}
                                        >
                                            {createOrderMutation.isPending ? (
                                                <Box
                                                    component={Icon}
                                                    icon="tabler:loader"
                                                    width="24"
                                                    height="24"
                                                    sx={{
                                                        animation: 'spin 1s linear infinite',
                                                        '@keyframes spin': {
                                                            '0%': { transform: 'rotate(0deg)' },
                                                            '100%': { transform: 'rotate(360deg)' },
                                                        },
                                                    }}
                                                />
                                            ) : (
                                                "Pay Now"
                                            )}
                                        </Button>
                                    </Box>
                                </Form>
                            )}
                        </Formik>

                        {!deliveryAddress && (
                            <Typography variant="caption" color="grey.500" textAlign="center" display="block" mt={0.75}>
                                Please add a delivery location to proceed
                            </Typography>
                        )}
                    </Box>
                </Grid2>
            </Grid2>

            {/* ── Address Dialog ── */}
            <AddressFormDialog
                open={addressDialogOpen}
                onClose={() => setAddressDialogOpen(false)}
                onSave={handleSaveAddress}
                initialValues={editingAddress ? {
                    full_name: editingAddress.full_name,
                    address: editingAddress.address_line_1,
                    pincode: editingAddress.postal_code,
                    city: editingAddress.city,
                    state: editingAddress.state,
                    address_type: editingAddress.address_type,
                    country: editingAddress.country,
                    contactNumber: editingAddress.phone,
                } : null}
            />

            {/* ── Delete Confirm Dialog ── */}
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} PaperProps={{ sx: { borderRadius: '12px' } }}>
                <DialogTitle sx={{ fontWeight: 600 }}>Delete Address</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary">
                        Are you sure you want to delete this address? This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ p: '1rem', pt: 0 }}>
                    <Button onClick={() => setDeleteDialogOpen(false)} sx={{ color: 'grey.600', textTransform: 'none' }}>Cancel</Button>
                    <Button onClick={handleConfirmDeleteAddress} variant="contained" sx={{ bgcolor: 'red.main', color: '#fff', '&:hover': { bgcolor: '#b01030' }, textTransform: 'none' }}>
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>


            {/* ── Snackbar ── */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
                    severity={snackbar.type}
                    variant="filled"
                    sx={{ borderRadius: '10px', fontWeight: 600 }}
                >
                    {snackbar.msg}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default SkillBoxCart;

